import { useCallback, useEffect, useState } from 'react';

import { Button, Dropdown, FieldLabel, Modal, showSnackbar, TextInput } from 'fluxo-ui';
import { EditIcon, PlusIcon, TrashIcon, UserGroupIcon } from 'fluxo-ui/icons';

import { USER_ROLES } from '../common/constants';
import { Badge, Card, EmptyState, PageHeader } from '../components/common';
import userService from '../services/user.service';
import { useAuthStore } from '../store/authStore';
import { formatDateTime } from '../utils/formatting';

import type { BadgeTone } from '../components/common/Badge';

const SEARCH_DEBOUNCE_MS = 350;

const statusTones: Record<string, BadgeTone> = {
    active: 'success',
    suspended: 'danger',
    pending_verification: 'warning',
    deleted: 'neutral',
};

const roleTones: Record<string, BadgeTone> = {
    super_admin: 'danger',
    org_admin: 'violet',
    org_moderator: 'warning',
    org_instructor: 'accent',
    org_member: 'neutral',
    individual_user: 'info',
};

const roleOptions = [
    { label: 'All Roles', value: 'all' },
    { label: 'Super Admin', value: USER_ROLES.SUPER_ADMIN },
    { label: 'Org Admin', value: USER_ROLES.ORG_ADMIN },
    { label: 'Org Moderator', value: USER_ROLES.ORG_MODERATOR },
    { label: 'Org Instructor', value: USER_ROLES.ORG_INSTRUCTOR },
    { label: 'Org Member', value: USER_ROLES.ORG_MEMBER },
    { label: 'Individual User', value: USER_ROLES.INDIVIDUAL_USER },
];

const statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Suspended', value: 'suspended' },
    { label: 'Pending Verification', value: 'pending_verification' },
];

const editRoleOptions = roleOptions.filter((r) => r.value !== 'all');
const editStatusOptions = statusOptions.filter((s) => s.value !== 'all');

const UsersPage: React.FC = () => {
    const { user } = useAuthStore();
    const [users, setUsers] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [limit] = useState(20);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<any>(null);
    const [editForm, setEditForm] = useState({ name: '', role: '', status: '' });
    const [saving, setSaving] = useState(false);

    // Create modal state
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        email: '',
        password: '',
        name: '',
        role: 'individual_user',
    });
    const [creating, setCreating] = useState(false);

    // Delete confirmation
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deletingUser, setDeletingUser] = useState<any>(null);

    const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const params: any = { page, limit };
            if (searchTerm) params.search = searchTerm;
            if (roleFilter !== 'all') params.role = roleFilter;
            if (statusFilter !== 'all') params.status = statusFilter;
            const response = await userService.listUsers(params);
            setUsers(response.users || []);
            setTotal(response.total || 0);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to fetch users', 'Error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [searchTerm, roleFilter, statusFilter, page, limit]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchTerm(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (user && ['super_admin', 'org_admin', 'org_moderator'].includes(user.role)) {
            fetchUsers();
        }
    }, [user, fetchUsers]);

    const handleEdit = (u: any) => {
        setEditingUser(u);
        setEditForm({
            name: u.profile?.name || '',
            role: u.role || 'individual_user',
            status: u.status || 'active',
        });
        setEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!editingUser) return;
        setSaving(true);
        try {
            await userService.updateUser(editingUser._id, {
                profile: { name: editForm.name },
                role: editForm.role,
                status: editForm.status,
            });
            showSnackbar('User updated successfully', 'Success', { type: 'success' });
            setEditModalOpen(false);
            setEditingUser(null);
            fetchUsers();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to update user', 'Error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCreateUser = async () => {
        if (!createForm.email || !createForm.password || !createForm.name) {
            showSnackbar('Please fill in all required fields', 'Error', { type: 'error' });
            return;
        }
        setCreating(true);
        try {
            await userService.createUser({
                email: createForm.email,
                password: createForm.password,
                profile: { name: createForm.name },
                role: createForm.role,
            });
            showSnackbar('User created successfully', 'Success', { type: 'success' });
            setCreateModalOpen(false);
            setCreateForm({ email: '', password: '', name: '', role: 'individual_user' });
            fetchUsers();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to create user', 'Error', { type: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = (u: any) => {
        setDeletingUser(u);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!deletingUser) return;
        try {
            await userService.deleteUser(deletingUser._id);
            showSnackbar('User deleted successfully', 'Success', { type: 'success' });
            setDeleteModalOpen(false);
            setDeletingUser(null);
            fetchUsers();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to delete user', 'Error', { type: 'error' });
        }
    };

    const totalPages = Math.ceil(total / limit);

    const getInitials = (name?: string, email?: string) => {
        const source = (name || email || '?').trim();
        const parts = source.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return source.substring(0, 2).toUpperCase();
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Users"
                description={`Manage users and their roles${total > 0 ? ` (${total} total)` : ''}`}
                className="mb-0"
                actions={
                    isSuperAdmin ? (
                        <Button label="Create User" leftIcon={PlusIcon} variant="primary" onClick={() => setCreateModalOpen(true)} />
                    ) : undefined
                }
            />

            <Card padding="sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <FieldLabel label="Search">
                        <TextInput
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.value)}
                            placeholder="Search by name or email..."
                        />
                    </FieldLabel>
                    <FieldLabel label="Filter by Role">
                        <Dropdown
                            value={roleFilter}
                            onChange={(e) => {
                                setRoleFilter(e.value);
                                setPage(1);
                            }}
                            options={roleOptions}
                            placeholder="Select role"
                        />
                    </FieldLabel>
                    <FieldLabel label="Filter by Status">
                        <Dropdown
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.value);
                                setPage(1);
                            }}
                            options={statusOptions}
                            placeholder="Select status"
                        />
                    </FieldLabel>
                </div>
            </Card>

            {/* Users List */}
            <Card padding="none" className="overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-slate-500 dark:text-slate-400">Loading...</div>
                ) : users.length === 0 ? (
                    <EmptyState icon={UserGroupIcon} title="No users found" description="Try adjusting your search or filters." />
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase">
                                            Name
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase">
                                            Email
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase">
                                            Role
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase">
                                            Last Login
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-300 uppercase">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {users.map((u: any) => (
                                        <tr key={u._id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <span className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-xs font-semibold flex items-center justify-center">
                                                        {getInitials(u.profile?.name, u.email)}
                                                    </span>
                                                    <span className="font-medium text-slate-900 dark:text-white">
                                                        {u.profile?.name || 'N/A'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{u.email || 'N/A'}</td>
                                            <td className="px-4 py-3">
                                                <Badge tone={roleTones[u.role] || 'neutral'}>{u.role?.replace(/_/g, ' ')}</Badge>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge tone={statusTones[u.status] || 'success'} dot>
                                                    {u.status?.replace(/_/g, ' ') || 'active'}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                                                {formatDateTime(u.lastLogin) || 'Never'}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        label=""
                                                        leftIcon={EditIcon}
                                                        size="sm"
                                                        variant="default"
                                                        layout="outlined"
                                                        onClick={() => handleEdit(u)}
                                                    />
                                                    {u._id !== user?._id && (
                                                        <Button
                                                            label=""
                                                            leftIcon={TrashIcon}
                                                            size="sm"
                                                            variant="danger"
                                                            layout="outlined"
                                                            onClick={() => handleDelete(u)}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                    Page {page} of {totalPages} ({total} users)
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        label="Previous"
                                        size="sm"
                                        variant="default"
                                        layout="outlined"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    />
                                    <Button
                                        label="Next"
                                        size="sm"
                                        variant="default"
                                        layout="outlined"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => p + 1)}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card>

            {/* Edit Modal */}
            <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit User">
                <div className="space-y-4 p-4">
                    <FieldLabel label="Name">
                        <TextInput
                            value={editForm.name}
                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.value }))}
                            placeholder="User name"
                        />
                    </FieldLabel>
                    <FieldLabel label="Role">
                        <Dropdown
                            value={editForm.role}
                            onChange={(e) => setEditForm((f) => ({ ...f, role: e.value }))}
                            options={editRoleOptions}
                        />
                    </FieldLabel>
                    <FieldLabel label="Status">
                        <Dropdown
                            value={editForm.status}
                            onChange={(e) => setEditForm((f) => ({ ...f, status: e.value }))}
                            options={editStatusOptions}
                        />
                    </FieldLabel>
                    <div className="flex justify-end gap-2 pt-4">
                        <Button label="Cancel" variant="default" layout="outlined" onClick={() => setEditModalOpen(false)} />
                        <Button label="Save Changes" variant="primary" onClick={handleSaveEdit} isLoading={saving} />
                    </div>
                </div>
            </Modal>

            {/* Create Modal */}
            <Modal isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} title="Create New User">
                <div className="space-y-4 p-4">
                    <FieldLabel label="Email" required>
                        <TextInput
                            value={createForm.email}
                            onChange={(e) => setCreateForm((f) => ({ ...f, email: e.value }))}
                            placeholder="user@example.com"
                        />
                    </FieldLabel>
                    <FieldLabel label="Password" required>
                        <TextInput
                            value={createForm.password}
                            onChange={(e) => setCreateForm((f) => ({ ...f, password: e.value }))}
                            placeholder="Minimum 8 characters"
                        />
                    </FieldLabel>
                    <FieldLabel label="Name" required>
                        <TextInput
                            value={createForm.name}
                            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.value }))}
                            placeholder="Full name"
                        />
                    </FieldLabel>
                    <FieldLabel label="Role">
                        <Dropdown
                            value={createForm.role}
                            onChange={(e) => setCreateForm((f) => ({ ...f, role: e.value }))}
                            options={editRoleOptions}
                        />
                    </FieldLabel>
                    <div className="flex justify-end gap-2 pt-4">
                        <Button label="Cancel" variant="default" layout="outlined" onClick={() => setCreateModalOpen(false)} />
                        <Button label="Create User" variant="primary" onClick={handleCreateUser} isLoading={creating} />
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Delete User">
                <div className="p-4">
                    <p className="text-slate-700 dark:text-slate-300 mb-4">
                        Are you sure you want to delete <strong>{deletingUser?.profile?.name || deletingUser?.email}</strong>? This action
                        cannot be undone.
                    </p>
                    <div className="flex justify-end gap-2">
                        <Button label="Cancel" variant="default" layout="outlined" onClick={() => setDeleteModalOpen(false)} />
                        <Button label="Delete" variant="danger" onClick={confirmDelete} />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default UsersPage;
